import { SocialItem } from "./SocialItem";

/** A run of consecutive posts by one contributor, newest first, as the feed ordered them. */
export interface PostGroup {
    /** Stable React key: the id of the run's first (newest) post. */
    key: string;
    items: SocialItem[];
}

/**
 * Contributor types whose posts are never folded together, however many they publish in a row.
 *
 * The President speaks for the institution, so those posts are the ones a reader is least likely to
 * want tucked away behind a "2 more" line - the opposite of the flooding problem this solves.
 */
export const NEVER_STACK_TYPES: ReadonlyArray<string> = ["president"];

/**
 * Splits a feed into runs of consecutive posts by the same contributor.
 *
 * Some accounts post in bursts and flood the feed, pushing everything else out of view. Collapsing a
 * run into one expandable card fixes that, and grouping is all this needs to be: the runs are
 * *consecutive* only, so the feed's chronological order is never rearranged - a contributor who
 * appears three separate times in a page stays three separate entries.
 *
 * Grouping is by contributor and nothing else. The network a post came from is deliberately not part
 * of the key: one account's Bluesky post followed by their tweet is still that account posting twice
 * in a row, and reads as flooding just the same.
 *
 * Page boundaries are not a problem here, which is worth stating because it looks as though they
 * would be. SocialItemsList keeps one cumulative array across pages and re-renders all of it, so a
 * burst split across a page boundary regroups as soon as the next page lands. Nothing has to be
 * negotiated with the API.
 *
 * Runs shorter than `minGroupSize` come back as one group per item, so a caller can render every
 * group the same way and let the length decide whether it stacks.
 */
export function groupConsecutivePosts(
    items: SocialItem[],
    minGroupSize: number = 2,
    neverStackTypes: ReadonlyArray<string> = NEVER_STACK_TYPES,
): PostGroup[] {
    const groups: PostGroup[] = [];
    let run: SocialItem[] = [];

    const flush = () => {
        if (! run.length) {
            return;
        }
        if (run.length >= minGroupSize) {
            groups.push({ key: String(run[0].id), items: run });
        } else {
            // Too short to stack: each item is its own group.
            run.forEach(item => groups.push({ key: String(item.id), items: [item] }));
        }
        run = [];
    };

    const stackable = (item: SocialItem) =>
        item.mep?.id != null && ! neverStackTypes.includes(String(item.mep?.type));

    items.forEach(item => {
        const previous = run[run.length - 1];
        // A contributor with no id cannot be matched against the next post, so it never joins a run -
        // better a card on its own than a stack built on a missing key. Same for a type that is never
        // stacked: it ends the run it meets and does not start one.
        const sameContributor = previous
            && stackable(previous)
            && stackable(item)
            && previous.mep.id === item.mep.id;

        if (! sameContributor) {
            flush();
        }
        run.push(item);
    });
    flush();

    return groups;
}
